import { Flag } from '@neo/primitives/icon'
import { Typography } from '@neo/primitives/typography'
import type { ApiResponse } from '@shared/types'
import { downloadFile } from '@shared/utils'
import type { AxiosError } from 'axios'
import { capitalize, humanize } from 'inflection'
import { queryClient } from 'v2/configs/rq-setup'
import { CurrencyRenderer } from 'v2/shared/components/currency'
import { billingService } from 'v2/shared/services/billing-service'
import { uploaderService } from 'v2/shared/services/uploader-service'
import { getErrorText } from 'v2/shared/utils/api-error-text'
import { getSettingsQueryKeys } from 'v2/shared/utils/query-key-management'
import type { RateData } from 'v2/types/api/billing'
import type { RUserProfile } from 'v2/types/api/settings'
import { AnalyticsEvents, type AnalyticsProperties, track } from 'v2/utils/analytics'
import { logger } from 'v2/utils/logger'
import { errorToast } from 'v2/utils/toasts'
import {
  Currency,
  PaymentStatus,
  PLAN_UPDATE_MAX_POLL_ATTEMPTS,
  PLAN_UPDATE_POLL_INTERVAL_MS,
  ValidationStatus,
  type ValidationStatusType,
  validateZipCodeWithRegex,
} from './billing.config'

type PaymentFailureFlow = 'recharge' | 'add_card' | 'plan_change' | '3ds_card'

// Fires PAYMENT_FAILED for a failed billing action. The billing error surface
// has no numeric error code, so we surface getErrorText() (the same text the
// user sees) plus the HTTP status, and let callers pass flow-specific extras
// (amount, currency, gateway).
export const trackPaymentFailed = (
  flow: PaymentFailureFlow,
  error: unknown,
  extra?: AnalyticsProperties,
) => {
  // Analytics in an error path must be provably unable to throw: getErrorText()
  // runs before track()'s own try/catch and throws on a null-ish error, which
  // would blow up the very catch block this instruments. Guard the whole body.
  try {
    const axiosError = error as AxiosError<ApiResponse<unknown>>
    track(AnalyticsEvents.PAYMENT_FAILED, {
      flow,
      status: axiosError?.response?.status,
      error_message: getErrorText(axiosError),
      ...extra,
    })
  } catch (e) {
    logger.error('trackPaymentFailed failed', e)
  }
}

/**
 * Polls using the query's refetch until the plan changes.
 * Resolves true if plan changed, false on timeout.
 */
export const pollForPlanUpdate = (
  refetch: () => Promise<{ data?: { plan?: string } }>,
  currentPlan: string,
  // Budget override. The defaults (3s x 5 = 15s) suit /change-plan, which flips the
  // plan synchronously and only needs the profile to catch up. A GATEWAY upgrade is
  // strictly slower: the plan flip happens on the capture webhook AFTER the credit
  // (razorpay_webhook.go order.paid), and refreshBillingAfterPayment already spends
  // 20s (or 40s when the gateway says "not captured yet") waiting on that credit —
  // so a 15s plan poll is guaranteed to give up first. Callers on a gateway rail
  // must pass a budget at least as long as the credit poll they sit behind.
  { intervalMs = PLAN_UPDATE_POLL_INTERVAL_MS, maxAttempts = PLAN_UPDATE_MAX_POLL_ATTEMPTS } = {},
): Promise<boolean> =>
  new Promise((resolve) => {
    let settled = false
    const finish = (upgraded: boolean) => {
      if (settled) return
      settled = true
      clearInterval(poll)
      clearTimeout(timeout)
      resolve(upgraded)
    }

    const check = async () => {
      try {
        const { data } = await refetch()
        if (data?.plan && data.plan.toLowerCase() !== currentPlan.toLowerCase()) finish(true)
      } catch {
        // transient failure, keep polling
      }
    }

    const poll = setInterval(check, intervalMs)
    const timeout = setTimeout(() => finish(false), intervalMs * maxAttempts)

    // Leading edge. setInterval's first tick is a whole interval away, and by the
    // time we get here the capture webhook has often already landed — so without
    // this, every plan-derived surface (the Payments tab's `visible: !isFreePlan`,
    // the Overview recent-transactions card) stays stale for at least one interval
    // after a successful upgrade payment. An already-paid account runs no poll at
    // all, which is why the receipt looked instant for them and delayed only for an
    // account just upgraded from trial.
    void check()
  })

export const mapStatusToBadgeText = (status: string) => {
  if (status === PaymentStatus.CREDITED) return 'Success'
  return humanize(capitalize(status))
}

export const getAccountId = () => {
  try {
    const profile = queryClient.getQueryData<RUserProfile>(getSettingsQueryKeys().profile)
    if (!profile) return 0
    return Number(profile?.organizations?.[0]?.org_plivo_account_id)
  } catch (error) {
    logger.error('Error getting account id:', error)
    return 0
  }
}

export const viewPDF = async (filePath: string) => {
  try {
    const response = await uploaderService.fetchInvoiceDocument({
      file_path: filePath,
    })
    const url = window.URL.createObjectURL(response)
    const newWindow = window.open('', '_blank')
    if (newWindow) {
      newWindow.location.href = url
      newWindow.addEventListener(
        'load',
        () => {
          URL.revokeObjectURL(url)
        },
        { once: true },
      )
    } else {
      URL.revokeObjectURL(url)
      logger.warn('viewPDF: Could not open PDF viewer. Popup might be blocked.')
    }
  } catch (error: unknown) {
    logger.error('viewPDF: Error downloading invoice PDF:', error)
    const axiosError = error as AxiosError<ApiResponse<Response>>
    // responseType: 'blob' makes error.response.data a Blob even for 500s
    // Parse it back to JSON so getErrorText can read errors.global_error
    if (axiosError?.response?.data instanceof Blob) {
      try {
        axiosError.response.data = JSON.parse(await axiosError.response.data.text())
      } catch {
        /* not JSON, getErrorText will use fallback */
      }
    }
    errorToast({ title: 'Error', description: getErrorText(axiosError) })
  }
}

export const downloadExcelInvoice = async (filePath: string) => {
  try {
    const response = await uploaderService.fetchInvoiceDocument({
      file_path: filePath,
    })
    const filePathParts = filePath?.split('/') || []
    const fileName =
      `Cx_Invoice_${filePathParts.at(-2)?.replace(/\s/g, '_').replace(/-/g, '_')}.xlsx` ||
      'Cx_Invoice.xlsx'

    await downloadFile('', fileName, response)
  } catch (error) {
    logger.error('Error downloading excel invoice:', error)
  }
}

const escapeCsvField = (field: string | number) => {
  const str = String(field)
  // Neutralize spreadsheet formula injection: a leading =, +, -, or @ makes
  // the cell execute as a formula when opened in Excel/Sheets. Prefix with '.
  const sanitized = /^[=+\-@]/.test(str) ? `'${str}` : str
  return sanitized.includes(',') || sanitized.includes('"') || sanitized.includes('\n')
    ? `"${sanitized.replace(/"/g, '""')}"`
    : sanitized
}

// All-destinations SIP Trunking sheets (Pylon #6431). The legacy console served both of
// these from the server, independently of its country selector; the new console had only
// the per-country card above, so a customer downloading "SIP Trunking rates" got one
// country. Core proxies Routing Central's export (outbound) and merges number-service
// with the inbound number groups (inbound).
export const downloadZentrunkOutboundRatesCsv = async () => {
  try {
    const blob = await billingService.getZentrunkOutboundRatesCsv()
    await downloadFile('', 'sip_trunking_outbound_rates.csv', blob)
  } catch (error) {
    logger.error('Error downloading SIP Trunking outbound rates:', error)
    errorToast({
      title: 'Download failed',
      description: 'Could not download the SIP Trunking outbound rates. Please try again.',
    })
  }
}

export const downloadZentrunkInboundRatesCsv = async () => {
  try {
    const blob = await billingService.getZentrunkInboundRatesCsv()
    await downloadFile('', 'sip_trunking_inbound_rates.csv', blob)
  } catch (error) {
    logger.error('Error downloading SIP Trunking inbound rates:', error)
    errorToast({
      title: 'Download failed',
      description: 'Could not download the SIP Trunking inbound rates. Please try again.',
    })
  }
}

export const downloadPricingCsv = (
  phoneNumbers: Array<{
    country: string
    country_iso: string
    country_code: number
    number_type: string
    capabilities: string[]
    setup_rate: string
    rental_rate: string
    inbound_voice_rate: string
    inbound_sms_rate: string
    inbound_mms_rate?: string
    status: string
  }>,
  channel: 'voice' | 'sms',
) => {
  const headers = [
    'Country',
    'Country ISO',
    'Country Code',
    'Phone Number Type',
    'Voice Enabled',
    'SMS Enabled',
    'MMS Enabled',
    'One Time Setup Price',
    'Rental Price / Month',
    'Inbound Voice Price / Minute',
    'Inbound SMS Price / Msg',
    'Inbound MMS Price / Msg',
    'Status',
  ]

  const rows = phoneNumbers
    .filter((n) => n.status !== 'DEPRECATED')
    .map((n) => [
      n.country,
      n.country_iso,
      n.country_code,
      n.number_type,
      n.capabilities.includes('voice') ? 'Yes' : 'No',
      n.capabilities.includes('sms') ? 'Yes' : 'No',
      n.capabilities.includes('mms') ? 'Yes' : 'No',
      n.setup_rate,
      n.rental_rate,
      n.capabilities.includes('voice') ? n.inbound_voice_rate : 'NA',
      n.capabilities.includes('sms') ? n.inbound_sms_rate : 'NA',
      n.capabilities.includes('mms') ? n.inbound_mms_rate || 'NA' : 'NA',
      n.status,
    ])
    .sort((a, b) => String(a[0]).localeCompare(String(b[0])))

  const csvContent = [headers, ...rows].map((row) => row.join(',')).join('\n')
  const blob = new Blob([csvContent], { type: 'text/csv' })
  const filename = channel === 'voice' ? 'inbound_voice_rates.csv' : 'inbound_sms_rates.csv'
  downloadFile('', filename, blob)
}

export const downloadSmsOutboundCsv = async () => {
  try {
    const response = await billingService.getSmsOutboundRates()
    const rates = response.data

    const headers = [
      'Country',
      'Country ISO',
      'Country Code',
      'Destination Network',
      'MCC',
      'MNC',
      'Rate',
    ]
    const rows = rates
      .map((r) => [
        r.country,
        r.country_iso,
        r.country_code,
        r.destination_network,
        r.mcc,
        r.mnc,
        r.rate,
      ])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])))

    const csvContent = [headers, ...rows]
      .map((row: (string | number)[]) => row.map(escapeCsvField).join(','))
      .join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    downloadFile('', 'sms_outbound_rates.csv', blob)
  } catch (error) {
    logger.error('Error downloading SMS outbound rates:', error)
  }
}

export const downloadVoiceOutboundCsv = async () => {
  try {
    const response = await billingService.getVoiceOutboundRates()
    const rates = response.data

    const headers = [
      'Country',
      'Country ISO',
      'Country Code',
      'Destination Prefix',
      'Origination Prefix',
      'Rate',
      'Voice Network Group',
      'Voice Unit (seconds)',
    ]
    const rows = rates
      .map((r) => [
        r.country,
        r.country_iso,
        r.country_code,
        (r.destination_prefix || []).join(';'),
        (r.origination_prefix || []).join(';'),
        r.rate,
        r.voice_network_group,
        r.voice_unit_in_seconds,
      ])
      .sort((a, b) => String(a[0]).localeCompare(String(b[0])))

    const csvContent = [headers, ...rows]
      .map((row: (string | number)[]) => row.map(escapeCsvField).join(','))
      .join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv' })
    downloadFile('', 'voice_outbound_rates.csv', blob)
  } catch (error) {
    logger.error('Error downloading voice outbound rates:', error)
  }
}

export const countryOptions = () =>
  (window.contactoCountryData || []).map((country) => ({
    label: country[0],
    value: country[1],
    icon: <Flag iso={country[1]} size={16} />,
  }))

export const getCountryName = (countryCode: string): string => {
  if (!countryCode || !window.contactoCountryData) return countryCode
  const country = window.contactoCountryData.find((country) => country[1] === countryCode)
  return country ? country[0] : countryCode
}

// State data using existing patterns from the codebase
const STATE_DATA = {
  US: [
    'Alabama',
    'Alaska',
    'Arizona',
    'Arkansas',
    'California',
    'Colorado',
    'Connecticut',
    'Delaware',
    'Florida',
    'Georgia',
    'Hawaii',
    'Idaho',
    'Illinois',
    'Indiana',
    'Iowa',
    'Kansas',
    'Kentucky',
    'Louisiana',
    'Maine',
    'Maryland',
    'Massachusetts',
    'Michigan',
    'Minnesota',
    'Mississippi',
    'Missouri',
    'Montana',
    'Nebraska',
    'Nevada',
    'New Hampshire',
    'New Jersey',
    'New Mexico',
    'New York',
    'North Carolina',
    'North Dakota',
    'Ohio',
    'Oklahoma',
    'Oregon',
    'Pennsylvania',
    'Rhode Island',
    'South Carolina',
    'South Dakota',
    'Tennessee',
    'Texas',
    'Utah',
    'Vermont',
    'Virginia',
    'Washington',
    'West Virginia',
    'Wisconsin',
    'Wyoming',
    'District of Columbia',
  ],
  IN: [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry',
  ],
} as const

export const getStateOptions = (countryCode: string) => {
  const states = STATE_DATA[countryCode as keyof typeof STATE_DATA] || []
  return [...states]
    .sort((a: string, b: string) => a.localeCompare(b))
    .map((state: string) => ({
      label: state,
      value: state,
    }))
}

export const indiaStateOptions = () => getStateOptions('IN')

export const formatCurrencyInput = (value: number) => {
  if (!value) return ''
  return new Intl.NumberFormat('en-US').format(value)
}

export const extractCurrencyValue = (value: string) => {
  if (!value) return 0
  const numericValue = value.replace(/[^\d]/g, '')
  return parseInt(numericValue, 10) || 0
}

export const getCurrencyParts = (currencyCode = 'USD') => {
  const formatted = new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currencyCode,
    currencyDisplay: 'symbol',
  }).formatToParts(100)

  const symbol = formatted.find((part) => part.type === 'currency')?.value || currencyCode
  return {
    symbol,
    code: currencyCode,
  }
}

export const getZipCodeStatus = (
  zipCode: string,
  selectedCountry: string,
  zipCodeValidation: { status?: boolean; errorMsg?: string } | undefined,
  isZipCodeValidating: boolean,
): ValidationStatusType => {
  if (!zipCode) return ValidationStatus.IDLE
  if (isZipCodeValidating) return ValidationStatus.LOADING
  if (selectedCountry === 'IN' && zipCode.length === 6) {
    // For India, only show valid/invalid after API validation completes
    // If zipCodeValidation is undefined, it means validation hasn't completed yet
    if (zipCodeValidation === undefined) return ValidationStatus.IDLE
    return zipCodeValidation?.status ? ValidationStatus.VALID : ValidationStatus.INVALID
  }
  if (validateZipCodeWithRegex(zipCode, selectedCountry)) {
    return ValidationStatus.VALID
  }
  return zipCode.length > 0 ? ValidationStatus.INVALID : ValidationStatus.IDLE
}

// Move state mappings to a constant
const INDIAN_STATE_MAPPINGS: Record<string, string> = {
  chhattisgarh: 'chattisgarh',
  puducherry: 'pondicherry',
} as const

interface ZipCodeApiResponse {
  Status: string
  PostOffice: Array<{ State?: string }>
}
// Extract Indian ZIP code validation logic
export const validateIndianZipCode = async (zipCode: string, state: string) => {
  const lowerCaseState = state.toLowerCase()

  if (lowerCaseState === 'ladakh') {
    return { status: true, errorMsg: '' }
  }

  try {
    const response: ZipCodeApiResponse = await billingService.verifyZipCode({ zipcode: zipCode })
    const data = Array.isArray(response) ? response[0] : response

    if (data?.Status === 'Error') {
      return { status: false, errorMsg: 'Incorrect Pincode' }
    }

    const stateFound = data.PostOffice.find((office: { State?: string }) => {
      const apiStateName = office.State?.toLowerCase()
      return (
        apiStateName === INDIAN_STATE_MAPPINGS[lowerCaseState] || apiStateName === lowerCaseState
      )
    })

    return stateFound
      ? { status: true, errorMsg: '' }
      : { status: false, errorMsg: 'The state is not the same as the PIN code.' }
  } catch {
    // Fail open: third-party postalpincode.in is unreliable (SSL cert lapsed on
    // 2026-05-21, blocking all Indian customers). Don't gate submit on its uptime.
    return { status: true, errorMsg: '' }
  }
}

// Auto-recharge fork used by Billing Overview's "Auto-recharge" action: Fork A
// (Stripe / non-mandate, `AutoRechargeModal`) vs Fork B (India Cashfree card
// mandates, `CardMandatesSheet`). This intentionally does NOT use
// `useIsCashfreeProvider()` — that hook governs the separate card-provider
// fork (which PSP tokenizes a saved card) and is unrelated to which
// auto-recharge UI an account should see. Mirrors the gating
// `pages/billing/settings/index.tsx`'s (now-removed) Auto Recharge tab used.
export const getAutoRechargeFork = (
  isPostpaid: boolean | undefined,
  isIndiaCurrency: boolean,
  isCashFreeAutoRecharge: boolean,
) => ({
  // India + the Cashfree-mandate flag on -> mandates (Fork B); everyone else
  // (including India with the flag off) -> the plain form (Fork A).
  isCashfreeAutoRechargeFork: isIndiaCurrency && isCashFreeAutoRecharge,
  // Postpaid accounts are invoiced (no self-serve recharge). India orgs
  // without the Cashfree-mandate flag have no compliant auto-recharge flow
  // yet (RBI e-mandate rules block a plain saved-card auto-charge), so the
  // action is hidden entirely rather than opening a non-functional form.
  showAutoRecharge: !isPostpaid && (!isIndiaCurrency || isCashFreeAutoRecharge),
})

export const formatRate = (rateData: RateData | string | null | undefined) => {
  // Handle null/undefined
  if (rateData == null) {
    return <Typography className="text-secondary text-s-400">Not Supported</Typography>
  }

  // Handle string format (backward compatibility for simple strings like "Included Free", "$0.0055/minute")
  if (typeof rateData === 'string') {
    return <Typography className="text-secondary text-s-400">{rateData}</Typography>
  }

  // Ensure rateData is a valid object with expected properties
  if (typeof rateData !== 'object' || rateData === null) {
    return <Typography className="text-secondary text-s-400">Not Supported</Typography>
  }

  const showStartsAt = Boolean(rateData.has_multiple_rates)
  const customPrefix = typeof rateData.prefix === 'string' ? rateData.prefix : undefined

  // Use Currency component for conversion (handles INR conversion automatically)
  const priceContent =
    rateData.value === null || rateData.value === undefined ? (
      <Typography className="text-secondary text-s-400">Not Supported</Typography>
    ) : rateData.value === 0 ? (
      <Typography className="text-secondary text-s-400">Free</Typography>
    ) : (
      <Typography className="text-secondary font-jetbrains text-xs-400 tabular-nums">
        <CurrencyRenderer
          value={rateData.value}
          decimalPlaces={0}
          options={{ maximumFractionDigits: 4 }}
        />
        {typeof rateData.unit === 'string' && rateData.unit && <span>/{rateData.unit}</span>}
      </Typography>
    )

  return (
    <div className="flex flex-col gap-1">
      {(showStartsAt || customPrefix) && (
        <Typography className="text-tertiary text-xs-400">
          {capitalize(customPrefix || 'STARTS AT')}
        </Typography>
      )}
      {priceContent}
    </div>
  )
}

// A pre-flip payment has no currency_payment_log row, so payment-service sends
// currency: '' with every currency_* field at 0 beside a real USD payment_amount
// (usecases/payment_receipt.go). Mirror ryuk (payments/views.py): local amounts
// for INR only, USD ledger figures otherwise, never scaled by today's rate.
export interface PaymentCurrencyAmounts {
  currency: string
  payment_amount: number
  gateway_fee: number
  currency_payment_amount: number
  currency_gateway_fee: number
}

export const resolvePaymentAmounts = <T extends PaymentCurrencyAmounts>(payment: T) => {
  const currency = payment.currency || Currency.USD
  const isLocalCurrency = currency === Currency.INR
  const paymentAmount = Number(
    (isLocalCurrency ? payment.currency_payment_amount : payment.payment_amount) || 0,
  )
  const gatewayFee = Number(
    (isLocalCurrency ? payment.currency_gateway_fee : payment.gateway_fee) || 0,
  )

  return {
    ...payment,
    currency,
    payment_amount: paymentAmount,
    gateway_fee: gatewayFee,
    total_amount: paymentAmount + gatewayFee,
  }
}
